// https://github.com/jerosoler/waveform-path/blob/master/src/waveform-path.js

export const getAudioData = (url: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioContext = new AudioContext();
    return fetch(url)
        .then((response) => response.arrayBuffer())
        .then(
            async (arrayBuffer) =>
                await audioContext.decodeAudioData(arrayBuffer),
        )
        .catch((error) => {
            console.error(error);
        });
};

export const linearPath = (
    audioBuffer: AudioBuffer,
    options: {
        channel?: number;
        samples?: number;
        height?: number;
        width?: number;
        top?: number;
        left?: number;
        type?: 'steps' | 'mirror' | 'bars';
        paths?: {
            d: string;
            sx?: number;
            sy: number;
            x: number;
            y?: number;
            ex?: number;
            ey: number;
            rx?: number;
            ry?: number;
            angle?: number;
            arc?: number;
            sweep?: number;
            minshow?: number;
            maxshow?: number;
            normalize?: boolean;
        }[];
        animation?: boolean;
        animationFrames?: number;
        normalize?: boolean;
    },
) => {
    const {
        channel = 0,
        samples = audioBuffer.length,
        height = 100,
        width = 800,
        top = 0,
        left = 0,
        type = 'steps',
        paths = [{ d: 'Q', sx: 0, sy: 0, x: 50, y: 100, ex: 100, ey: 0 }],
        animation = false,
        animationFrames = 10,
        normalize = true,
    } = options;

    const framesData = getFramesData(
        audioBuffer,
        channel,
        animation,
        animationFrames,
    );
    const numberArray: number[][] = framesData.map((floatArray) =>
        Array.from(floatArray),
    );
    const filteredData = getFilterData(numberArray, samples);
    const normalizeData = normalize
        ? getNormalizeData(filteredData)
        : filteredData;

    let path = ``;

    const fixHeight = type != 'bars' ? (height + top * 2) / 2 : height + top;
    const fixWidth = width / samples;
    const pathsLength = paths.length;
    const fixPathsLength = type == 'mirror' ? pathsLength * 2 : pathsLength;

    const normalizeDataLength = normalizeData.length;

    for (let f = 0; f < normalizeDataLength; f++) {
        if (f > 0) {
            const pathLength = path.length;
            const lastValue = path.charAt(pathLength - 1);
            if (lastValue == ';' || pathLength === 0) {
                path += ' M 0 0 ;';
            } else {
                path += ';';
            }
        }

        let last_pos_x = -9999;
        let last_pos_y = -9999;

        for (let i = 0; i < samples; i++) {
            const positive = type != 'bars' ? (i % 2 ? 1 : -1) : 1;
            let mirror = 1;
            for (let j = 0; j < fixPathsLength; j++) {
                let k = j;
                if (j >= pathsLength) {
                    k = j - pathsLength;
                    mirror = -1;
                }
                paths[k].minshow = paths[k].minshow ?? 0;
                paths[k].maxshow = paths[k].maxshow ?? 1;
                paths[k].normalize = paths[k].normalize ?? false;
                const normalizeDataValue = paths[k].normalize
                    ? 1
                    : normalizeData[f][i];
                if (
                    (paths[k].minshow ?? 0) <= normalizeData[f][i] &&
                    (paths[k].maxshow ?? 0) >= normalizeData[f][i]
                ) {
                    switch (paths[k].d) {
                        // LineTo Commands
                        case 'L': {
                            const pos_x =
                                i * fixWidth +
                                (fixWidth * (paths[k].sx ?? 0)) / 100 +
                                left;
                            const pos_y =
                                fixHeight +
                                ((normalizeDataValue * paths[k].sy) / 100) *
                                    (type != 'bars' ? height / 2 : height) *
                                    -positive *
                                    mirror;

                            //const end_pos_x = ((i+1) * fixWidth) - (fixWidth*(1-(paths[k].ex/100))) + left;
                            const end_pos_x =
                                i * fixWidth +
                                (fixWidth * (paths[k].ex ?? 1)) / 100 +
                                left;
                            const end_pos_y =
                                fixHeight +
                                ((normalizeDataValue * paths[k].ey) / 100) *
                                    (type != 'bars' ? height / 2 : height) *
                                    -positive *
                                    mirror;

                            if (pos_x !== last_pos_x || pos_y !== last_pos_y) {
                                path += `M ${pos_x} ${pos_y} `;
                            }

                            path += `L ${end_pos_x} ${end_pos_y} `;

                            last_pos_x = end_pos_x;
                            last_pos_y = end_pos_y;
                            break;
                        }

                        case 'H': {
                            const pos_x =
                                i * fixWidth +
                                (fixWidth * (paths[k].sx ?? 0)) / 100 +
                                left;
                            const pos_y =
                                fixHeight +
                                ((normalizeDataValue * (paths[k].y ?? 0)) /
                                    100) *
                                    (type != 'bars' ? height / 2 : height) *
                                    -positive *
                                    mirror;

                            //const end_pos_x = ((i+1) * fixWidth) - (fixWidth*(1-(paths[k].ex/100))) + left;
                            const end_pos_x =
                                i * fixWidth +
                                (fixWidth * (paths[k].ex ?? 0)) / 100 +
                                left;
                            const end_pos_y = pos_y;

                            if (pos_x !== last_pos_x || pos_y !== last_pos_y) {
                                path += `M ${pos_x} ${pos_y} `;
                            }

                            path += `H ${end_pos_x} `;

                            last_pos_x = end_pos_x;
                            last_pos_y = end_pos_y;
                            break;
                        }

                        case 'V': {
                            const pos_x =
                                i * fixWidth +
                                (fixWidth * paths[k].x) / 100 +
                                left;
                            const pos_y =
                                fixHeight +
                                ((normalizeDataValue * paths[k].sy) / 100) *
                                    (type != 'bars' ? height / 2 : height) *
                                    -positive *
                                    mirror;

                            const end_pos_x = pos_x;
                            const end_pos_y =
                                fixHeight +
                                ((normalizeDataValue * paths[k].ey) / 100) *
                                    (type != 'bars' ? height / 2 : height) *
                                    -positive *
                                    mirror;

                            if (pos_x !== last_pos_x || pos_y !== last_pos_y) {
                                path += `M ${pos_x} ${pos_y} `;
                            }

                            path += `V ${end_pos_y} `;

                            last_pos_x = end_pos_x;
                            last_pos_y = end_pos_y;
                            break;
                        }

                        // Cubic Bézier Curve Commands
                        case 'C': {
                            const pos_x =
                                i * fixWidth +
                                (fixWidth * (paths[k].sx ?? 0)) / 100 +
                                left;
                            const pos_y =
                                fixHeight -
                                ((fixHeight * paths[k].sy) / 100) * positive;

                            const center_pos_x =
                                i * fixWidth +
                                (fixWidth * paths[k].x) / 100 +
                                left;
                            const center_pos_y =
                                fixHeight +
                                ((normalizeDataValue * (paths[k].y ?? 0)) /
                                    100) *
                                    (type != 'bars' ? height : height * 2) *
                                    -positive *
                                    mirror;

                            //const end_pos_x = ((i+1) * fixWidth) - (fixWidth*(1-(paths[k].ex/100))) + left;
                            const end_pos_x =
                                i * fixWidth +
                                (fixWidth * (paths[k].ex ?? 0)) / 100 +
                                left;
                            const end_pos_y =
                                fixHeight -
                                ((fixHeight * paths[k].ey) / 100) * positive;

                            if (pos_x !== last_pos_x || pos_y !== last_pos_y) {
                                path += `M ${pos_x} ${pos_y} `;
                            }

                            path += `C ${pos_x} ${pos_y} ${center_pos_x} ${center_pos_y} ${end_pos_x} ${end_pos_y} `;

                            last_pos_x = end_pos_x;
                            last_pos_y = end_pos_y;
                            break;
                        }

                        // Quadratic Bézier Curve Commands
                        case 'Q': {
                            const pos_x =
                                i * fixWidth +
                                (fixWidth * (paths[k].sx ?? 0)) / 100 +
                                left;
                            const pos_y =
                                fixHeight +
                                ((normalizeDataValue * paths[k].sy) / 100) *
                                    (type != 'bars' ? height / 2 : height) *
                                    -positive *
                                    mirror;

                            const center_pos_x =
                                i * fixWidth +
                                (fixWidth * paths[k].x) / 100 +
                                left;
                            const center_pos_y =
                                fixHeight +
                                ((normalizeDataValue * (paths[k].y ?? 0)) /
                                    100) *
                                    (type != 'bars' ? height : height * 2) *
                                    -positive *
                                    mirror;

                            //const end_pos_x = ((i+1) * fixWidth) - (fixWidth*(1-(paths[k].ex/100))) + left;
                            const end_pos_x =
                                i * fixWidth +
                                (fixWidth * (paths[k].ex ?? 0)) / 100 +
                                left;
                            const end_pos_y =
                                fixHeight +
                                ((normalizeDataValue * paths[k].ey) / 100) *
                                    (type != 'bars' ? height / 2 : height) *
                                    -positive *
                                    mirror;

                            if (pos_x !== last_pos_x || pos_y !== last_pos_y) {
                                path += `M ${pos_x} ${pos_y} `;
                            }

                            path += `Q ${center_pos_x} ${center_pos_y} ${end_pos_x} ${end_pos_y} `;

                            last_pos_x = end_pos_x;
                            last_pos_y = end_pos_y;
                            break;
                        }

                        // Elliptical Arc Curve Commands
                        case 'A': {
                            const pos_x =
                                i * fixWidth +
                                (fixWidth * (paths[k].sx ?? 0)) / 100 +
                                left;
                            const pos_y =
                                fixHeight +
                                ((normalizeDataValue * paths[k].sy) / 100) *
                                    (type != 'bars' ? height / 2 : height) *
                                    -positive *
                                    mirror;

                            //const end_pos_x = ((i+1) * fixWidth) - (fixWidth*(1-(paths[k].ex/100))) + left;
                            const end_pos_x =
                                i * fixWidth +
                                (fixWidth * (paths[k].ex ?? 0)) / 100 +
                                left;
                            const end_pos_y =
                                fixHeight +
                                ((normalizeDataValue * paths[k].ey) / 100) *
                                    (type != 'bars' ? height / 2 : height) *
                                    -positive *
                                    mirror;

                            if (pos_x !== last_pos_x || pos_y !== last_pos_y) {
                                path += `M ${pos_x} ${pos_y} `;
                            }
                            const rx = ((paths[k].rx ?? 0) * fixWidth) / 100;
                            const ry = ((paths[k].ry ?? 0) * fixWidth) / 100;
                            let sweep = paths[k].sweep;
                            if (positive == -1) {
                                if (sweep == 1) {
                                    sweep = 0;
                                } else {
                                    sweep = 1;
                                }
                            }
                            if (mirror == -1) {
                                if (sweep == 1) {
                                    sweep = 0;
                                } else {
                                    sweep = 1;
                                }
                            }
                            path += `A ${rx} ${ry} ${paths[k].angle} ${paths[k].arc} ${sweep} ${end_pos_x} ${end_pos_y} `;

                            last_pos_x = end_pos_x;
                            last_pos_y = end_pos_y;
                            break;
                        }

                        // ClosePath Commands
                        case 'Z':
                            path += 'Z ';
                            break;

                        default:
                            break;
                    }
                }
            }
        }
    }
    return path;
};

const getFramesData = (
    audioBuffer: AudioBuffer,
    channel: number,
    animation: boolean,
    animationFrames: number,
) => {
    const rawData = audioBuffer.getChannelData(channel);

    const framesData = [];
    if (animation) {
        const frames = audioBuffer.sampleRate / animationFrames;
        for (let index = 0; index < rawData.length; index += frames) {
            const partRaw = rawData.slice(index, index + frames);
            framesData.push(partRaw);
        }
    } else {
        framesData.push(rawData);
    }

    return framesData;
};

const getFilterData = (framesData: number[][], samples: number) => {
    const filteredData = [];
    const framesDataLength = framesData.length;
    for (let f = 0; f < framesDataLength; f++) {
        const blockSize = Math.floor(framesData[f].length / samples); // the number of samples in each subdivision
        const filteredDataBlock = [];
        for (let i = 0; i < samples; i++) {
            const blockStart = blockSize * i; // the location of the first sample in the block
            let sum = 0;
            for (let j = 0; j < blockSize; j++) {
                sum = sum + Math.abs(framesData[f][blockStart + j]); // find the sum of all the samples in the block
            }
            filteredDataBlock.push(sum / blockSize); // divide the sum by the block size to get the average
        }
        filteredData.push(filteredDataBlock);
    }
    return filteredData;
};

const getNormalizeData = (filteredData: number[][]) => {
    const multipliers = [];
    const filteredDataLength = filteredData.length;
    for (let i = 0; i < filteredDataLength; i++) {
        const multiplier = Math.max(...filteredData[i]);
        multipliers.push(multiplier);
    }
    const maxMultiplier = Math.pow(Math.max(...multipliers), -1);

    const normalizeData = [];
    for (let i = 0; i < filteredDataLength; i++) {
        const normalizeDataBlock = filteredData[i].map(
            (n) => n * maxMultiplier,
        );
        normalizeData.push(normalizeDataBlock);
    }
    return normalizeData;
};
